/**
 * @file     addressFormat.ts
 * @readme   ./CLAUDE.md
 * @purpose  Canonical, display-safe formatting of an MLS street-address line.
 *           Single source of truth so the unit/suite designator renders
 *           identically everywhere (cards, detail page, dashboard tabs, API
 *           address suggestions, schema.org JSON-LD, CRM sync), plus the
 *           seller address opt-out applied at every read boundary.
 */

import { coarsenCoord } from './redactListingDetail';

/**
 * Normalize an MLS unit/suite value into its display token, deciding whether a
 * leading "#" belongs.
 *
 * The MLS `UnitNumber` field carries two very different kinds of value:
 *   1. A bare suite number — "302", "12A", "1205" — which reads naturally as
 *      "#302".
 *   2. A descriptive designator the board already spells out — strata lot
 *      "SL 13" (presale), penthouse "PH 3", townhouse "TH 5", "Lot 4",
 *      "Unit 2", "Suite 100". Prefixing these with "#" produces nonsense like
 *      "#SL 13".
 *
 * We distinguish them by the first character after normalization: a leading
 * digit is a suite number and gets "#"; a leading letter is already a label and
 * is rendered verbatim. This is deliberately a structural rule, not a
 * hand-maintained keyword list (SL/PH/TH/…), so a new board designator can
 * never silently regress back into "#<label>".
 *
 * Also defends against feeds that already embedded a "#": a leading "#"/space
 * run is stripped first so we never emit "##302".
 *
 * @returns the display token (e.g. "#302", "SL 13") or `null` when the value is
 *          absent/blank.
 */
export function formatUnitDesignator(unitNumber: string | null | undefined): string | null {
  if (unitNumber == null) return null;
  const normalized = unitNumber.trim().replace(/\s+/g, ' ');
  const bare = normalized.replace(/^#+\s*/, '');
  if (!bare) return null;
  return /^\d/.test(bare) ? `#${bare}` : bare;
}

/** Civic + unit components of a street address, as carried on MLS rows. */
export interface StreetAddressParts {
  unitNumber?: string | null;
  streetNumber?: string | null;
  streetDirPrefix?: string | null;
  streetName?: string | null;
  streetSuffix?: string | null;
}

/**
 * Compose the canonical street-address line from its parts. Order-independent
 * object input so every call site passes exactly the fields it has (cards omit
 * the directional prefix; the detail page includes it) without positional
 * ambiguity.
 *
 * Civic parts are joined in postal order: number, directional prefix, name,
 * suffix. Whitespace is trimmed/collapsed; blank parts are dropped. The unit
 * designator (see {@link formatUnitDesignator}) leads, separated by an en-dash.
 * The line never includes the city — callers append locality themselves.
 *
 * Examples:
 *   { streetNumber: '1234', streetName: 'Main', streetSuffix: 'St' }
 *     -> "1234 Main St"
 *   { unitNumber: '302', streetNumber: '1234', streetName: 'Main', streetSuffix: 'St' }
 *     -> "#302 – 1234 Main St"
 *   { unitNumber: 'SL 13', streetNumber: '320', streetDirPrefix: 'North',
 *     streetName: 'NANAIMO', streetSuffix: 'Street' }
 *     -> "SL 13 – 320 North NANAIMO Street"
 *   { unitNumber: 'PH 3' }                 -> "PH 3"
 *   {}                                     -> ""
 */
export function formatStreetLine(parts: StreetAddressParts): string {
  const civic = [parts.streetNumber, parts.streetDirPrefix, parts.streetName, parts.streetSuffix]
    .filter((s): s is string => Boolean(s && s.trim()))
    .map((s) => s.trim())
    .join(' ');
  const unit = formatUnitDesignator(parts.unitNumber);
  if (!civic) return unit ?? '';
  return unit ? `${unit} – ${civic}` : civic;
}

export interface WithholdableAddress extends StreetAddressParts {
  postalCode?: string | null;
  internetAddressDisplayYN?: boolean | null;
  latitude?: number | null;
  longitude?: number | null;
  parcelNumber?: string | null;
  taxLegalDescription?: string | null;
}

/** RESO InternetAddressDisplayYN. NULL means displayable, like every other opt-out flag here. */
export function addressDisplayAllowed(listing: Pick<WithholdableAddress, 'internetAddressDisplayYN'>): boolean {
  return listing.internetAddressDisplayYN !== false;
}

/**
 * Applied once at each read boundary (detail, cards, pins) so every surface
 * inherits the seller's address opt-out. Postal code goes too: a BC postal
 * code narrows to a block face. A rooftop point, a PID or a legal description
 * each resolve to the address on their own, so coordinates are coarsened like
 * the anonymous detail redaction and the parcel identifiers are dropped. Keys
 * the input does not carry are not added.
 */
export function withholdAddress<T extends WithholdableAddress>(listing: T): T {
  if (addressDisplayAllowed(listing)) return listing;
  const out: WithholdableAddress = {
    ...listing,
    unitNumber: null,
    streetNumber: null,
    streetDirPrefix: null,
    streetName: null,
    streetSuffix: null,
    postalCode: null,
  };
  if ('latitude' in listing) out.latitude = coarsenCoord(listing.latitude ?? null);
  if ('longitude' in listing) out.longitude = coarsenCoord(listing.longitude ?? null);
  if ('parcelNumber' in listing) out.parcelNumber = null;
  if ('taxLegalDescription' in listing) out.taxLegalDescription = null;
  return out as T;
}

const WITHHELD_ROW_COLUMNS = [
  'unit_number',
  'street_number',
  'street_dir_prefix',
  'street_name',
  'street_suffix',
  'postal_code',
  'unparsed_address',
  'parcel_number',
  'tax_legal_description',
] as const;

/**
 * {@link withholdAddress} for raw `mls_properties` / canonical-view rows, for
 * readers (alert emails, digests, typeahead) that compose an address straight
 * from snake_case columns. The row must select `internet_address_display_yn`.
 */
export function withholdAddressRow<T extends object>(row: T): T {
  const r = row as Record<string, unknown>;
  if (r.internet_address_display_yn !== false) return row;
  const out: Record<string, unknown> = { ...r };
  for (const col of WITHHELD_ROW_COLUMNS) {
    if (col in r) out[col] = null;
  }
  if ('latitude' in r) out.latitude = coarsenCoord((r.latitude as number | null) ?? null);
  if ('longitude' in r) out.longitude = coarsenCoord((r.longitude as number | null) ?? null);
  return out as T;
}

/**
 * PostgREST predicate that drops withheld-address rows. For address lookups
 * (typeahead, address resolve, address search), where returning the row at
 * all confirms the street the seller withheld.
 */
export function excludeAddressWithheld<Q extends { not: (column: string, operator: string, value: unknown) => Q }>(q: Q): Q {
  return q.not('internet_address_display_yn', 'is', false);
}
